import { EnvironmentProviders, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

const RELOADED_KEY = 'glxy-chunk-retry:reloaded';
const RETRY_KEY_PREFIX = 'glxy-chunk-retry:url:';

export function provideChunkLoadRetry(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(() => {
      const router = inject(Router);
      router.events
        .pipe(filter((e): e is NavigationError => e instanceof NavigationError))
        .subscribe((e) => {
          if (isChunkLoadFailure(e.error)) {
            retryOnChunkFailure(router, e);
          } else if (isUnmatchedRoute(e.error)) {
            redirectUnmatchedRoute(router, e);
          }
        });
    }),
  ]);
}

export function isChunkLoadFailure(error: unknown): boolean {
  let err = error instanceof Error ? error : null;
  while (err) {
    if (
      err.name === 'ChunkLoadError' ||
      /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/.test(
        err.message,
      )
    ) {
      return true;
    }
    err = err.cause instanceof Error ? err.cause : null;
  }
  return false;
}

export function retryOnChunkFailure(router: Router, e: NavigationError, reload: () => void = () => location.reload()): void {
  const retriedKey = RETRY_KEY_PREFIX + e.url;
  if (!safeGet(retriedKey)) {
    safeSet(retriedKey, '1');
    void router.navigateByUrl(e.url);
    return;
  }
  if (!safeGet(RELOADED_KEY)) {
    safeSet(RELOADED_KEY, '1');
    reload();
  }
}

export function isUnmatchedRoute(error: unknown): boolean {
  return error instanceof Error && /NG04002/.test(error.message);
}

export function redirectUnmatchedRoute(router: Router, e: NavigationError): void {
  if (e.url === '/') {
    return;
  }
  void router.navigateByUrl('/');
}

function safeGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    return;
  }
}
